// @ts-nocheck
import { useState } from 'react'
import { useQuery } from 'react-query'
import { 
  Activity, 
  Network, 
  Server,
  ArrowUpRight,
  RefreshCw
} from 'lucide-react'
import { apiService } from '../services/api'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

const Monitoring = () => {
  const [selectedInterface, setSelectedInterface] = useState('')
  const { data: networkSummary, refetch: refetchNetwork } = useQuery('networkSummary',
    () => apiService.getNetworkSummary(),
    { refetchInterval: 30000 }
  )

  const { data: dnsStats } = useQuery('dnsStats',
    () => apiService.getDnsStats(24),
    { refetchInterval: 60000 }
  )

  const interfaces = networkSummary?.data?.interfaces || []
  const activeInterface = selectedInterface || interfaces.find((iface: any) => iface.is_active)?.name || interfaces[0]?.name || ''
  const activeInterfaceData = interfaces.find((iface: any) => iface.name === activeInterface)
  const trafficData = activeInterfaceData ? [{
    time: new Date().toLocaleTimeString(),
    rx: Math.round(((activeInterfaceData.stats?.rx_bytes || activeInterfaceData.rx_bytes || 0) / 1048576) * 100) / 100,
    tx: Math.round(((activeInterfaceData.stats?.tx_bytes || activeInterfaceData.tx_bytes || 0) / 1048576) * 100) / 100,
  }] : []
  const formatBytes = (bytes: number) => {
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let value = Number(bytes || 0)
    let unit = 0
    while (value >= 1024 && unit < units.length - 1) {
      value = value / 1024
      unit++
    }
    return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 2)} ${units[unit]}`
  }
  const cacheHits = dnsStats?.data?.current?.cache_hits || 0
  const cacheMisses = dnsStats?.data?.current?.cache_misses || 0
  const hitRate = cacheHits + cacheMisses > 0 ? Math.round((cacheHits / (cacheHits + cacheMisses)) * 100) : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Live Monitoring</h2>
          <p className="text-slate-500">Real-time network and DNS statistics</p>
        </div>
        <button
          onClick={() => refetchNetwork()}
          className="flex items-center space-x-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Network Interfaces</p>
              <h3 className="text-2xl font-bold text-slate-900 mt-1">
                {networkSummary?.data?.total_interfaces || 0}
              </h3>
              <p className="text-sm text-green-600 mt-1">
                {networkSummary?.data?.active_interfaces || 0} active
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Network className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">DNS Queries (24h)</p>
              <h3 className="text-2xl font-bold text-slate-900 mt-1">
                {dnsStats?.data?.current?.query_count || 0}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {dnsStats?.data?.current?.qps || 0} QPS
              </p>
            </div>
            <div className="p-3 bg-sky-50 rounded-lg">
              <Activity className="h-6 w-6 text-sky-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Cache Hit Rate</p>
              <h3 className="text-2xl font-bold text-slate-900 mt-1">{hitRate}%</h3>
              <p className="text-sm text-slate-500 mt-1">{cacheHits} hits / {cacheMisses} misses</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <Server className="h-6 w-6 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Total Traffic</p>
              <h3 className="text-2xl font-bold text-slate-900 mt-1">
                {networkSummary?.data?.total_rx_human || '0 B'}
              </h3>
              <p className="text-sm text-slate-500 mt-1">RX / {networkSummary?.data?.total_tx_human || '0 B'} TX</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <ArrowUpRight className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Network Interfaces */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Network Interfaces</h3>
          <select value={activeInterface} onChange={(e) => setSelectedInterface(e.target.value)} className="px-3 py-2 border rounded-lg">
            {interfaces.map((iface: any) => <option key={iface.name} value={iface.name}>{iface.name}</option>)}
          </select>
        </div>
        <div className="overflow-hidden border border-slate-200 rounded-lg">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">Interface</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">IP</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">MAC</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">Speed</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">RX</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">TX</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {interfaces.map((iface: any) => (
                <tr key={iface.name} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{iface.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{iface.ip_address || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{iface.mac_address || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{iface.speed_mbps || 0} Mbps</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatBytes(iface.stats?.rx_bytes || iface.rx_bytes || 0)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatBytes(iface.stats?.tx_bytes || iface.tx_bytes || 0)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${iface.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {iface.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Traffic Chart */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Network Traffic</h3>
        <p className="text-sm text-slate-500 mb-4">Interface: {activeInterface || 'N/A'}</p>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trafficData}>
              <defs>
                <linearGradient id="colorRx" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorTx" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="time" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }} 
              />
              <Area
                type="monotone" 
                dataKey="rx" 
                name="RX MB"
                stroke="#0ea5e9" 
                fillOpacity={1} 
                fill="url(#colorRx)" 
                strokeWidth={2}
              />
              <Area
                type="monotone" 
                dataKey="tx" 
                name="TX MB"
                stroke="#22c55e" 
                fillOpacity={1} 
                fill="url(#colorTx)" 
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

export default Monitoring
